import { useState } from 'react';
import {
  PaymentMethodType,
  PaymentOrigin,
  SetupFutureUsage,
} from '@weave/schema-gen-ts/dist/schemas/payments/messages/values/values.pb';
import { StartSyncPaymentResponse } from '@weave/schema-gen-ts/dist/schemas/payments/v2/start_payment.pb';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { InvoiceModel } from '@frontend/api-invoices';
import { PaymentsV2 } from '@frontend/api-payments';
import { PersonsV3 } from '@frontend/api-person';
import { PaymentIntent } from '@frontend/api-weave-pay';
import { HttpError } from '@frontend/fetch';
import { useTranslation } from '@frontend/i18n';
import { isPaidOrPendingInvoice } from '@frontend/payments-invoice-calculations';
import { useAlert } from '@frontend/design-system';

export enum PaymentIntentSyncErrors {
  RequestAlreadyCompleted = 'request is already completed',
}

export type UseStartTerminalPaymentProps = {
  invoice: InvoiceModel | undefined;
  person: Person | undefined;
  readerId: string | undefined;
  onRefetchInvoice?: () => Promise<InvoiceModel | undefined>;
  onInvoiceAlreadyPaid?: () => void;
  generatePaymentUuid: () => string;
};

export type StartPaymentResponse = Promise<
  | {
      paymentId: string;
      paymentIntent: PaymentIntent;
    }
  | undefined
>;

export const useStartTerminalPaymentV2 = ({
  invoice,
  person,
  readerId,
  onRefetchInvoice,
  onInvoiceAlreadyPaid,
  generatePaymentUuid,
}: UseStartTerminalPaymentProps) => {
  const { t } = useTranslation('payments');
  const alerts = useAlert();

  const [paymentResponse, setPaymentResponse] = useState<StartSyncPaymentResponse>();

  const errorAlert = (message: string) =>
    alerts.error({
      autoDismissAfter: 12000,
      message,
    });

  const personEmail = person ? PersonsV3.PersonHelpers.getEmailContactType(person) : '';

  const { mutateAsync: startSyncRequestedPayment, isLoading } =
    PaymentsV2.Mutations.useStartSyncRequestedPaymentMutation({});

  const startPayment = async () => {
    if (onRefetchInvoice) {
      const refreshedInvoice = await onRefetchInvoice();
      if (isPaidOrPendingInvoice(refreshedInvoice)) {
        errorAlert(t('Invoice is already pending or paid.'));
        onInvoiceAlreadyPaid?.();
        return;
      }
    }

    setPaymentResponse(undefined);

    if (!invoice?.id) {
      throw new Error('Invoice ID is required to start terminal payment');
    }

    try {
      const { paymentId, paymentIntent, paymentStatus } = await startSyncRequestedPayment({
        requestId: invoice?.id,
        startPayment: {
          amount: invoice?.billedAmount ?? 0,
          paymentId: generatePaymentUuid(),
          origin: PaymentOrigin.TERMINAL,
          setupFutureUsage: SetupFutureUsage.OFF_SESSION,
          person: {
            email: personEmail ?? '',
            id: person?.personId,
            pmId: person?.personPmid,
          },
          paymentMethodType: PaymentMethodType.PAYMENT_METHOD_CARD,
          terminalReaderId: readerId,
        },
      });

      setPaymentResponse({ paymentIntent, paymentId, paymentStatus });

      if (onRefetchInvoice) {
        await onRefetchInvoice();
      }

      return { paymentId, paymentIntent, paymentStatus };
    } catch (err) {
      if (err instanceof HttpError && err.data && typeof err.data !== 'string') {
        throw new Error(t('Failed to create payment. Please try again or use a different payment method.'));
      } else {
        throw err;
      }
    }
  };

  return { startPayment, isStartingPayment: isLoading, paymentIntent: paymentResponse?.paymentIntent };
};
